import React, { useRef, useState } from 'react';
import Slider, { Settings } from 'react-slick';

import { Svg } from 'src/components/Svg/Svg';
import { Modal } from 'src/components/Modal/Modal';
import { ModalHeader } from 'src/components/Modal/ModalHeader';
import { ModalBody } from 'src/components/Modal/ModalBody';
import { RichText } from 'src/components/RichText/RichText';

import { PromotionsBlock } from './components/PromotionsBlock';
import classes from './ProductPromotions.module.scss';
import type { ProductPromotion } from './utils/product-detail.types';

export type ProductPromotionsProps = {
    promotions: ProductPromotion[];
};

/**
 * ProductPromotions component
 * @param props
 * @constructor
 */
export function ProductPromotions(props: ProductPromotionsProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const sliderRef = useRef<Slider>(null);
    const [slideIndex, setSlideIndex] = useState(0);
    const promotionsCount = props.promotions.length;
    const showDots = promotionsCount > 1;

    // Configure slider settings
    const settings: Settings = {
        accessibility: true,
        arrows: false,
        autoplay: true,
        autoplaySpeed: 4000,
        className: classes.promotionsSlider,
        fade: true,
        dots: showDots,
        draggable: false,
        infinite: true,
        pauseOnHover: true,
        pauseOnFocus: true,
        slidesToScroll: 1,
        slidesToShow: 1,
        speed: 500,
        beforeChange(_currentSlide: number, nextSlide: number) {
            setSlideIndex(nextSlide);
        },
    };

    return (
        <div className={classes.promotions}>
            {showDots && (
                <small className={classes.promotionsMeta}>
                    {slideIndex + 1} of {promotionsCount} Available Offers
                </small>
            )}

            <button
                data-ui-position={showDots}
                type="button"
                aria-label="Open promotions modal for more information"
                className={classes.promotionsButton}
                onClick={() => setIsModalOpen(true)}
            >
                <Svg name="info-outline" />
            </button>

            <Slider ref={sliderRef} {...settings}>
                {props.promotions.map((promotion) => {
                    return (
                        <div key={promotion.ruleId}>
                            <h3 className={classes.promotionsTitle}>{promotion.pdpLabel}</h3>
                            {promotion.pdpDescription && (
                                <p className={classes.promotionsDesc}>
                                    <RichText className={classes.promotionTerms} content={promotion.pdpDescription} />
                                </p>
                            )}
                        </div>
                    );
                })}
            </Slider>

            <Modal onDismiss={() => setIsModalOpen(false)} isOpen={isModalOpen} type="sidebar-right">
                <ModalHeader title="Promotions" onDismiss={() => setIsModalOpen(false)} />
                <ModalBody>
                    {props.promotions.map((promotion) => {
                        return <PromotionsBlock key={promotion.ruleId} promotion={promotion} />;
                    })}
                </ModalBody>
            </Modal>
        </div>
    );
}
